import React, { memo, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";

import { ReactComponent as RefreshIcon } from "@assets/ic_refresh.svg";
import { paginationLimit } from "@const/feed";
import { TABS } from "@components/PostComments/constans";
import { useInfiniteScroll } from "@hooks/useInfiniteScroll";
import { CommentSkeleton } from "@components/Comment";
import { convertTimeToSeconds } from "@helpers/dates";
import Message from "@pages/Livestream/components/Sidebar/components/LiveChat/components/Chat/components/Message";
import { getLivestreamMessages } from "@redux/livestreamMessages/actions";
import { AUDIENCE, HOST } from "@const/livestreams";

const LivestreamMessages = ({
  activeTab,
  streamUniqueKey,
  isLiveStreamChatDisplayed,
}) => {
  const [visibleStreamMessages, setVisibleStreamMessages] = useState([]);
  const [time, setTime] = useState(0);
  const dispatch = useDispatch();
  const { inView } = useInView();
  const scrollRef = useRef(null);
  const commentsEndRef = useRef(null);

  const { ref: currentPlayer } = useSelector(
    ({ playerReducer }) => playerReducer,
  );

  const isPlaying = () => {
    try {
      if (currentPlayer && typeof currentPlayer.paused === "function") {
        return !currentPlayer.paused();
      }
      return false;
    } catch (e) {
      return false;
    }
  };
  const { allMessages, messagesById, totalCount, isLoading } = useSelector(
    ({ livestreamMessagesReducer }) => livestreamMessagesReducer,
  );

  useEffect(() => {
    if (!currentPlayer) return;

    const handleTimeUpdate = () => {
      const currentTime = Math.floor(currentPlayer.currentTime());
      setTime(currentTime);
    };

    currentPlayer.on("timeupdate", handleTimeUpdate);

    return () => {
      currentPlayer.off("timeupdate", handleTimeUpdate);
    };
  }, [currentPlayer]);

  useEffect(() => {
    const messagesToShow = allMessages.filter((id) => {
      const convertedTime = convertTimeToSeconds(messagesById[id]?.streamTime);
      return convertedTime <= time;
    });
    setVisibleStreamMessages(messagesToShow);
  }, [time]);

  useEffect(() => {
    if (isLiveStreamChatDisplayed && streamUniqueKey) {
      dispatch(
        getLivestreamMessages({
          uniqueKey: streamUniqueKey,
          limit: paginationLimit,
        }),
      );
    }
  }, [isLiveStreamChatDisplayed, streamUniqueKey]);

  // Scroll to bottom when new messages appears
  useEffect(() => {
    if (inView || !visibleStreamMessages || !isPlaying()) return;
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleStreamMessages]);

  const getMoreMessages = () => {
    dispatch(
      getLivestreamMessages({
        uniqueKey: streamUniqueKey,
        isNextPage: true,
        streamTime: messagesById?.[allMessages.at(-1)]?.streamTime,
        limit: paginationLimit,
      }),
    );
  };

  useEffect(() => {
    if (allMessages && !isPlaying()) {
      setVisibleStreamMessages(allMessages);
    }
  }, [allMessages]);

  const { trigger } = useInfiniteScroll({
    isLoading,
    totalCount,
    callback: getMoreMessages,
  });

  if (activeTab === TABS.COMMENTS) {
    return null;
  }
  return (
    <div ref={scrollRef}>
      <InfiniteScroll
        inverse
        dataLength={visibleStreamMessages?.length}
        scrollableTarget="scrollableDiv"
        className={classNames(
          visibleStreamMessages?.length < 2 && "overflow-visible",
        )}
      >
        {visibleStreamMessages?.map((id) => {
          const messageSnapshot = JSON.parse(messagesById[id].messageSnapshot);
          const streamUserType =
            messageSnapshot.streamParticipant?.userId ===
            messageSnapshot.author.id
              ? HOST
              : AUDIENCE;

          return (
            <Message
              {...messageSnapshot}
              key={messageSnapshot.id}
              streamUserType={streamUserType}
            />
          );
        })}
        {trigger}
        {isLoading && [
          <CommentSkeleton key="comment-skeleton-0" />,
          <CommentSkeleton key="comment-skeleton-1" />,
          <CommentSkeleton key="comment-skeleton-2" />,
          <CommentSkeleton key="comment-skeleton-3" />,
          <CommentSkeleton key="comment-skeleton-4" />,
        ]}
        <div ref={commentsEndRef} />
      </InfiniteScroll>

      {!inView ? (
        <div
          onClick={() =>
            scrollRef?.current?.scroll({ top: 0, behavior: "smooth" })
          }
        >
          <RefreshIcon className="text-primary-opposite rotate-180" />
        </div>
      ) : null}
    </div>
  );
};

export default memo(LivestreamMessages);
